import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';

interface ProposalSummary {
  id: string;
  opportunityId: string;
  title: string;
  status: string;
  dealValue: string;
  currency: string;
  discountPercent: string;
  currentVersionId: string | null;
  approvalNote: string | null;
}

interface ProposalVersion {
  id: string;
  versionNumber: number;
  body: string;
  authorId: string | null;
  createdAt: string | null;
}

interface ProposalDetail {
  summary: ProposalSummary;
  versions: ProposalVersion[];
}

/**
 * Story 8.8 — proposals under an opportunity. Lists proposals + their
 * versions and exposes the state-machine transitions (send, approve,
 * reject, feedback). Routed as `/opportunities/:id/proposals`.
 */
export function ProposalsPage() {
  const { id: opportunityId } = useParams<{ id: string }>();
  const [list, setList] = useState<ProposalSummary[]>([]);
  const [detail, setDetail] = useState<ProposalDetail | null>(null);

  const refresh = useCallback(async () => {
    if (!opportunityId) return;
    const { data } = await apiClient.get<ProposalSummary[]>('/proposals', {
      params: { opportunityId },
    });
    setList(data);
  }, [opportunityId]);

  useEffect(() => {
    refresh().catch(() => setList([]));
  }, [refresh]);

  async function openDetail(id: string) {
    const { data } = await apiClient.get<ProposalDetail>(`/proposals/${id}`);
    setDetail(data);
  }

  async function transition(action: 'send' | 'approve' | 'reject' | 'feedback') {
    if (!detail) return;
    const body: Record<string, string> = {};
    if (action === 'reject') {
      const note = window.prompt('Motivo de rechazo');
      if (!note) return;
      body.note = note;
    }
    if (action === 'feedback') {
      const outcome = window.prompt('ACCEPTED o REJECTED');
      if (!outcome) return;
      body.outcome = outcome;
    }
    await apiClient.post(`/proposals/${detail.summary.id}/${action}`, body);
    await openDetail(detail.summary.id);
    await refresh();
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Propuestas</h1>
      <ul className="divide-y rounded-lg border bg-card">
        {list.map((p) => (
          <li key={p.id} className="flex items-center justify-between px-4 py-3">
            <button type="button" onClick={() => openDetail(p.id)} className="text-left">
              <div className="font-medium">{p.title}</div>
              <div className="text-xs text-muted-foreground">
                {p.currency} {p.dealValue} · {p.status}
              </div>
            </button>
          </li>
        ))}
        {list.length === 0 && (
          <li className="px-4 py-6 text-center text-sm text-muted-foreground">—</li>
        )}
      </ul>

      {detail && (
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">{detail.summary.title}</h2>
              <div className="text-xs text-muted-foreground">
                {detail.summary.status} · {detail.versions.length} versión(es)
              </div>
            </div>
            <div className="flex gap-2">
              {detail.summary.status === 'DRAFT' && (
                <Button size="sm" onClick={() => transition('send')}>
                  Enviar
                </Button>
              )}
              {detail.summary.status === 'PENDING_APPROVAL' && (
                <>
                  <Button size="sm" onClick={() => transition('approve')}>
                    Aprobar
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => transition('reject')}>
                    Rechazar
                  </Button>
                </>
              )}
              {(detail.summary.status === 'SENT' ||
                detail.summary.status === 'UNDER_REVIEW') && (
                <Button size="sm" variant="outline" onClick={() => transition('feedback')}>
                  Feedback cliente
                </Button>
              )}
            </div>
          </div>
          <pre className="mt-3 max-h-64 overflow-auto rounded-md bg-muted p-3 text-xs">
            {detail.versions[0]?.body}
          </pre>
        </div>
      )}
    </div>
  );
}
